/* global Popper */
import { Controller } from 'stimulus';
import { PopoverContainer } from '@/js/util';
import { PopperModifiers } from '@/js/util/popper';

const popoverContainer = PopoverContainer.dropdown('moodboard--dropdown-container');

export default class extends Controller {
  static values = {
    placement: String,
    padding: Number,
    distance: Number,
    limit: String,
    detached: Boolean,
  };

  /** @type {MoodboardDropdownTriggerController} */
  trigger;

  connect() {
    if (!this.detachedValue) {
      return;
    }

    // In detached mode we should move element into popoverContainer on first open, which is happening in two steps:
    // - we first override default value getter to move an element instead
    // - once moved element is connected to the page - we should immediately open it
    this.inPopoverContainer ? (this.visible = true) : this.#overrideValueSetter();
  }

  #overrideValueSetter() {
    Object.defineProperty(this, 'visible', {
      set(value) {
        if (value) {
          const copy = this.element.cloneNode(true);
          this.element.remove();
          popoverContainer.add(copy);
        }
      },
      configurable: true,
    });
  }

  hide() {
    this.visible = false;
  }

  get inPopoverContainer() {
    return popoverContainer.has(this.element);
  }

  get visible() {
    return !this.element.classList.contains('hidden');
  }

  set visible(value) {
    if (value === this.visible) {
      return;
    }

    value ? this.#createInstance(this.#popperModifiers) : this.#destroyInstance();

    this.element.classList.toggle('hidden', !value);
    this.trigger.visible = value;
  }

  #createInstance(modifiers) {
    this.instance = Popper.createPopper(this.trigger.element, this.element, {
      placement: this.placementValue,
      modifiers,
    });
  }

  #destroyInstance() {
    this.instance?.destroy();
  }

  get #popperModifiers() {
    const modifiers = new PopperModifiers();
    modifiers.offset([0, this.distanceValue]);

    if (this.hasPaddingValue) {
      modifiers.preventOverflow(this.paddingValue);
    }

    if (this.#isInlineLimit) {
      modifiers.inlineLimit(this.paddingValue);
    }

    if (this.#isBlockLimit) {
      modifiers.blockLimit(this.paddingValue);
    }

    return modifiers.toArray();
  }

  get #isInlineLimit() {
    return this.limitValue === 'inline' || this.limitValue === 'both';
  }

  get #isBlockLimit() {
    return this.limitValue === 'block' || this.limitValue === 'both';
  }
}
